<script setup lang="ts">
import { logout } from '@/shared/auth/session';
import { isCognitoEnabled } from '@/shared/auth/cognitoFeatureFlag';
import { useIsLoggedIn } from '@/shared/auth/useIsLoggedIn';
import { AppContext } from '@/models/AppContext';
import { useCommissioningNavStore } from '@/modules/commissioning/stores/commissioningNavStore';
import { useRouter } from 'vue-router';
import { ref, watch } from 'vue';
import NavBarDrawer from './NavBarDrawer.vue';
import ContactUs from '../views/ContactUs.vue';

const props = defineProps<{ visible: boolean }>();
const showContactUs = ref(false);
const commissioningNavStore = useCommissioningNavStore();

watch(
  () => props.visible,
  (newVal) => {
    if (!newVal) {
      showContactUs.value = false;
    }
  },
);

watch(
  () => commissioningNavStore.contactUsViaNavRequested,
  (requested) => {
    if (requested) {
      showContactUs.value = true;
    }
  },
);

const emit = defineEmits<(e: 'update:visible', value: boolean) => void>();
const close = () => emit('update:visible', false);

const loggedIn = useIsLoggedIn(AppContext.ComApp);
const router = useRouter();

const onLogout = async () => {
  await logout(AppContext.ComApp);
  close();
};

const goToProfile = () => {
  router.push({ name: 'Profile' });
};
</script>

<template>
  <NavBarDrawer :visible="visible" @update:visible="$emit('update:visible', $event)">
    <a-row align="top" type="flex" class="nav__overlay__item">
      <h3>
        <router-link :to="{ name: 'ComWelcome' }" class="nav__overlay__item__link" @click="close">
          Home
        </router-link>
      </h3>
    </a-row>

    <a-row align="top" type="flex" class="nav__overlay__item">
      <h3>
        <div @click="showContactUs = true" class="nav__overlay__item__link">Contact Us</div>
      </h3>
      <ContactUs v-model:visible="showContactUs" />
    </a-row>

    <a-row
      v-if="isCognitoEnabled && loggedIn"
      align="top"
      type="flex"
      class="nav__overlay__item"
    >
      <h3>
        <a class="nav__overlay__item__link" data-tn="nav-bar-overlay-profile" @click="goToProfile">
          Profile
        </a>
      </h3>
    </a-row>

    <a-row v-if="loggedIn" align="top" type="flex" class="nav__overlay__item">
      <h3>
        <a class="nav__overlay__item__link" data-tn="nav-bar-overlay-logout" @click="onLogout">
          Log out
        </a>
      </h3>
    </a-row>
  </NavBarDrawer>
</template>

<style lang="less" scoped>
.nav__overlay__item {
  margin-bottom: 11px;

  .nav__overlay__item__link {
    color: @color-primary;
    font-weight: 400;
    cursor: pointer;
  }
}
</style>
